import React from 'react';
import { Link } from 'react-router-dom';
import AssessmentPage from './AssessmentPage';

const LearningAssessment = () => (
  <AssessmentPage
    seo={{
      title: 'Dyslexia & Learning Assessment Coffs Harbour | Clinical Minds Psychology',
      description: 'Assessment for dyslexia and other learning difficulties, formally diagnosed as Specific Learning Disorders. Cognitive and academic testing with plain-language feedback. Coffs Harbour, visiting clinics and telehealth.',
      canonicalUrl: 'https://www.clinicalminds.net/learning-assessment'
    }}
    heroTitle="Learning and Cognitive Assessment"
    heroSubtitle=""
    intro={
      <p className="body-medium">
        When a child is working harder than everyone else for smaller results, the problem is almost never effort. A learning assessment finds out what's actually happening: how your child thinks, reasons, remembers, reads, writes and works with numbers, and exactly where the breakdown between their ability and their output is occurring. The result is not a label for the sake of a label. It's a precise understanding that changes how your child is taught, and often how they see themselves.
      </p>
    }
    afterIntro={
      <>
        <section className="assess-section assess-alt scroll-reveal" data-testid="assess-dyslexia">
          <div className="container assess-narrow">
            <h2 className="heading-1">Searching for a dyslexia assessment?</h2>
            <div className="assess-body">
              <p className="body-medium">
                Dyslexia is the everyday name for what's formally diagnosed as Specific Learning Disorder with impairment in reading. You might also have heard of dysgraphia (formally SLD with impairment in written expression) and dyscalculia (SLD with impairment in mathematics). My assessment covers all three in full. I use the formal names in reports because they're what schools, NDIS and other supports recognise, but I'll always explain what they mean in plain language, and the feedback you receive is written for humans, not filing cabinets.
              </p>
            </div>
          </div>
        </section>
        <section className="assess-section scroll-reveal" data-testid="assess-what-involves">
          <div className="container assess-narrow">
            <h2 className="heading-1">What the assessment involves</h2>
            <div className="assess-body">
              <p className="body-medium">
                A learning assessment looks at two things together: how your child thinks and learns (cognitive assessment), and where their reading, writing and maths skills currently sit (academic assessment). Comparing the two is what tells us whether a learning disorder is present, and more importantly, what kind of support will actually help. Like all my assessments, this happens across two sessions on different days, because one snapshot is never the whole picture, and it starts with an hour with parents before any testing begins.
              </p>
            </div>
          </div>
        </section>
      </>
    }
    whoFor={
      <p className="body-medium">
        Children and young people who are struggling with reading, spelling, writing or maths despite good teaching and genuine effort. Students whose school has raised concerns, or whose NAPLAN and classroom results don't match the capable kid you know at home. It's also for families who need a formal assessment of overall cognitive and adaptive functioning, including where intellectual disability is a question. I also assess adults.
      </p>
    }
    whatsInvolved={
      <p className="body-medium">
        Assessment typically includes cognitive assessment with standardised tools such as the WISC-V, detailed academic achievement testing across reading, written expression and mathematics, and language and literacy measures where needed. I gather information from school and home, because a learning profile only makes sense in context. I prioritise time with the person being assessed: an intake session plus at least two assessment sessions, because good assessment can't be done in a single visit. You then receive a plain-language feedback session and a full written report. Where results indicate a diagnosis, it's named formally and precisely, for example SLD with impairment in reading, because precise naming is what unlocks the right support.
      </p>
    }
    reportUsedFor={
      <p className="body-medium">
        The report explains your child's learning profile in practical terms: what's strong, what's effortful, and what specific teaching approaches and classroom adjustments will help. It's written to support school learning adjustments and NCCD evidence requirements, disability provisions for exams where applicable, NDIS access where relevant, and a shared plan between home and school.
      </p>
    }
    feesContent={
      <>
        <p className="body-medium">
          Assessment fees depend on the scope of the assessment. See our <Link to="/fees" className="assess-link">fees page</Link> for information about funding options, or <Link to="/contact" className="assess-link">contact us</Link> and we'll talk it through.
        </p>
      </>
    }
    ctaText="Enquire about a learning assessment"
    enquiryParam="learning"
    extraSections={
      <section id="intellectual-disability" className="assess-section scroll-reveal" data-testid="assess-intellectual-disability">
        <div className="container assess-narrow">
          <span className="section-label">INTELLECTUAL DISABILITY</span>
          <h2 className="heading-1">Intellectual Disability Assessment</h2>
          <div className="assess-body">
            <p className="body-medium">
              Sometimes the question is broader than reading, writing or maths: whether overall thinking and everyday skills are developing in a way that needs formal recognition and support. An intellectual disability assessment brings together standardised cognitive assessment with structured measures of adaptive functioning, the practical skills of daily life at home, at school and in the community. The same unhurried, affirming approach applies: the aim is a clear, respectful picture of a person's strengths and support needs, not a label for its own sake. Where results support a formal diagnosis, it is named precisely, and the report can support NDIS access, school funding and adjustments, and planning ahead. Children, young people and adults are all assessed.
            </p>
          </div>
        </div>
      </section>
    }
    faqs={[
      {
        question: 'Is this a dyslexia assessment?',
        answer: "Yes, in formal terms. Dyslexia is the everyday name for SLD with impairment in reading, and that's the formal diagnosis a report would name. The assessment covers reading, written expression and mathematics, so you get the complete learning picture, not just one piece."
      },
      {
        question: 'School says to wait and see. Should we?',
        answer: "Sometimes a period of targeted support at school is a sensible first step, and sometimes assessment is the faster route to clarity. If you're unsure, contact us and we'll talk through which makes sense for your child. An assessment provides clear information either way, and there's no obligation to proceed after we talk."
      },
      {
        question: 'My child is bright. Can they still have a learning disorder?',
        answer: "Absolutely. Specific learning disorders are specific: they affect particular skills, not intelligence. Bright children often mask difficulties with effort for years, which is exhausting and easy for schools to miss."
      },
      {
        question: 'Will my child find the assessment stressful?',
        answer: "We work hard to make it comfortable. Sessions are paced to the child with breaks as needed, there's no pass or fail, and everything is explained in child-friendly terms. Parents are welcome to raise any worries beforehand so we can plan around them."
      }
    ]}
  />
);

export default LearningAssessment;
